// AccountBootstrapFlow — the ONE boot path (contract 07 §8, ledger A2/A5/A11). Folds v3
// `BootstrapV2Flow` (620 ln) + the dying v1 flow (195 ln) into one command class — a DECOMPOSITION:
// scenarios, cached-id fast paths, pick order, `account_id=0` resolve, deferred-compositions
// staleness guard and `finalize` logic are LIFTED from v3, not re-derived. Unlike v3 it branches on
// `ApiError.code`/`kind` (never prose), NEVER auto-logs-out (`error` + retry; 401 bubbles), imports
// no other-feature store / router / axios (hydrates its OWN stores, dispatches the rest via
// `hydratorRegistry`, takes route-name + post-boot hooks by INJECTION), and drops the dual path.
// A 200 with failed/cold sections is a DEGRADED READY (BE-ACC-06): the flow records
// `{section, retryable}` on `sessionStore.degradedSections` and still reaches `ready` — a section
// failing is never a boot failure, and `empty` sections are legitimate data, not failures.
// DECISION GATE (ORP-18630-R1): STARTABLE version — no cutover (18641), no client RBAC fallback (18613).
//
// The hydrate / pick-workspace / finalize half of that lift is shared with the two switch flows and
// lives in `flowToolkit` (internal to `flows/`). What is NOT shared stays here: the scenarios, the
// cached-id fast paths, and the DEFERRED compositions — the extra fetch plus its `isCurrentWorkspace`
// staleness guard are a cold-boot optimisation a switch deliberately does not have.
import type { AccountDetail, AccountItem, UserResponse } from '../models/account.types'
import type { MappedBootstrapResult } from '../models/bootstrap.types'
import type { WorkspaceItem } from '../models/workspace.types'
import { AccountService } from '../service/AccountService'
import { BootstrapService } from '../service/BootstrapService'
import type { PackageSignal } from '../service/PackageService'
import { useAccountStore, type AccountStore } from '../stores/accountStore'
import { useSessionStore, type SessionStore } from '../stores/sessionStore'
import { useWorkspaceStore, type WorkspaceStore } from '../stores/workspaceStore'

import { COMPOSITIONS_SECTIONS, FlowToolkit, toApiError, userKeyOf } from './flowToolkit'
import { dispatchBootstrap } from './hydratorRegistry'

import { ApiError } from '@/shared/http'

// Machine error codes (ledger A2 — never string-sniff prose).
const WORKSPACE_NOT_FOUND = 'WORKSPACE_NOT_FOUND'
const NO_ACCESSIBLE_ACCOUNT = 'NO_ACCESSIBLE_ACCOUNT'
const ACCOUNT_ID_REQUIRED = 'ACCOUNT_ID_REQUIRED'

/** Identify-hook context — fires after account + accountDetail land, before workspace hydration
 *  (parity-critical timing, v3 `:184`/`:214`/`:260`). */
export interface BootstrapIdentifyContext {
  user: UserResponse | null
  account: AccountItem | null
  accountDetail: AccountDetail | null
}

// Post-boot side-effects 07 owns the trigger for but does not implement here (they live behind
// `shared/lib/analytics` / 04 `setLocale` / the shell GTM seam, wired by later tasks). Every hook is
// optional so the flow is startable + unit-testable; `onUnauthorized` is the 401 bubble, not a logout.
export interface AccountBootstrapHooks {
  identify?: (ctx: BootstrapIdentifyContext) => void
  syncWorkspaceLocale?: () => void
  /** Fires exactly once per successful boot (§8), with the identity already loaded by bootstrap. */
  trackLoginOrSignup?: (ctx: BootstrapIdentifyContext) => void
  /** Pricing / limit / onboarding SIGNAL — app stays `ready`; the modal is a credits/account UI. */
  onPackageSignal?: (signal: PackageSignal) => void
  /** 401 bubble to auth's `onUnauthorized` (03/06). 07 NEVER logs out on a boot error. */
  onUnauthorized?: (error: ApiError) => void
  /** No accessible account (404) / account_id required (400). The caller decides the empty-state
   *  destination — `isSysAdmin` lets it differ for a sysadmin vs. the regular onboarding empty
   *  state. Boot still reaches `ready`. */
  onNoAccessibleAccount?: (ctx: { isSysAdmin: boolean }) => void | Promise<void>
  /** Deferred-compositions best-effort failure — never fails the boot. */
  onDeferredError?: (error: unknown) => void
}

export interface AccountBootstrapFlowOptions {
  bootstrapService?: BootstrapService
  accountService?: AccountService
  session?: SessionStore
  accountStore?: AccountStore
  workspaceStore?: WorkspaceStore
  dispatch?: typeof dispatchBootstrap
  hooks?: AccountBootstrapHooks
  /** Router-free seam deciding eager vs deferred compositions (v3 read `router.currentRoute`). */
  isCompositionsRoute?: () => boolean
}

export class AccountBootstrapFlow {
  private readonly bootstrapService: BootstrapService
  private readonly accountService: AccountService
  private readonly session: SessionStore
  private readonly accountStore: AccountStore
  private readonly workspaceStore: WorkspaceStore
  private readonly dispatch: typeof dispatchBootstrap
  private readonly hooks: AccountBootstrapHooks
  private readonly isCompositionsRoute: () => boolean
  /** The hydrate / pick / finalize toolkit shared with the two switch flows. Its `hooks` is THIS
   *  flow's object by reference, so a late-registered `onPackageSignal` still fires. */
  private readonly toolkit: FlowToolkit

  constructor(options: AccountBootstrapFlowOptions = {}) {
    this.bootstrapService = options.bootstrapService ?? new BootstrapService()
    this.accountService = options.accountService ?? new AccountService()
    this.session = options.session ?? useSessionStore()
    this.accountStore = options.accountStore ?? useAccountStore()
    this.workspaceStore = options.workspaceStore ?? useWorkspaceStore()
    this.dispatch = options.dispatch ?? dispatchBootstrap
    this.hooks = options.hooks ?? {}
    this.isCompositionsRoute = options.isCompositionsRoute ?? (() => false)
    this.toolkit = new FlowToolkit({
      accountStore: this.accountStore,
      workspaceStore: this.workspaceStore,
      dispatch: this.dispatch,
      hooks: this.hooks,
    })
  }

  /** The one boot path. `reload=false` short-circuits a concurrent double-boot (one boot per token,
   *  §14); `reload=true` forces `refresh` on every section AND bypasses the short-circuit. */
  public async execute(reload = false): Promise<void> {
    if (!reload && (this.session.isBooting || this.session.hasBooted)) return
    this.session.beginBoot()
    try {
      await this.selectAndRunScenario(reload)
    } catch (error) {
      await this.handleBootError(error)
      return
    }
    this.session.markReady()
    this.hooks.trackLoginOrSignup?.(this.identifyContext())
  }

  /** Cached-id fast paths → the right scenario, with the two documented error fallbacks. */
  private async selectAndRunScenario(reload: boolean): Promise<void> {
    const cachedAccountId = this.getCachedAccountId()
    const cachedWorkspaceId = this.getCachedWorkspaceId()
    if (cachedAccountId && cachedWorkspaceId !== undefined) {
      try {
        await this.executeFullBoot(cachedAccountId, cachedWorkspaceId, reload)
      } catch (error) {
        if (!isCode(error, WORKSPACE_NOT_FOUND)) throw error
        // The cached workspace is gone for THIS session — exclude it from the re-pick (ORP-22838).
        // Without the exclusion the fallback re-selects it (`oldWorkspaceForAccount` still names
        // it, and the list it matches against is the persisted one whenever the re-fetched
        // `account_core` is degraded), re-issues the same request, and the second
        // WORKSPACE_NOT_FOUND — thrown from inside this handler — reaches the error surface.
        await this.executeAccountThenWorkspace(cachedAccountId, reload, cachedWorkspaceId)
      }
    } else if (cachedAccountId) {
      try {
        await this.executeAccountThenWorkspace(cachedAccountId, reload)
      } catch (error) {
        if (!isNoAccount(error)) throw error
        await this.executeFirstLogin(reload)
      }
    } else {
      await this.executeFirstLogin(reload)
    }
  }
  /** Both ids cached (warm refresh): one aggregated fetch for account + workspace. */
  private async executeFullBoot(
    accountId: number,
    workspaceId: number | string,
    reload: boolean,
  ): Promise<void> {
    const includeCompositions = this.isCompositionsRoute()
    const result = await this.bootstrapService.bootInitialFull(accountId, workspaceId, {
      refresh: reload,
      includeCompositions,
    })
    this.recordDegraded(result)
    this.toolkit.hydrateAccountLevel(result)
    this.setAccount(accountId)
    this.hooks.identify?.(this.identifyContext())
    const ws = this.toolkit.pickWorkspace(workspaceId)
    this.commitWorkspace(ws)
    this.toolkit.hydrateWorkspaceLevel(result)
    this.hooks.syncWorkspaceLocale?.()
    this.toolkit.finalize()
    this.startDeferredCompositions(accountId, workspaceId, reload, includeCompositions)
  }

  /** Account cached, workspace not (or the cached workspace 404'd): account fetch, then workspace.
   *  `skipWorkspaceId` is the id that just 404'd, kept out of the re-pick. */
  private async executeAccountThenWorkspace(
    accountId: number,
    reload: boolean,
    skipWorkspaceId?: number | string,
  ): Promise<void> {
    const accountResult = await this.bootstrapService.bootAccountOnly(accountId, {
      refresh: reload,
    })
    this.recordDegraded(accountResult)
    this.toolkit.hydrateAccountLevel(accountResult)
    this.setAccount(accountId)
    this.hooks.identify?.(this.identifyContext())
    await this.loadWorkspaceLevel(reload, skipWorkspaceId)
    this.toolkit.finalize()
  }

  /** First login: `account_id=0`, the server resolves the account, read back from session_access. */
  private async executeFirstLogin(reload: boolean): Promise<void> {
    const accountResult = await this.bootstrapService.bootAccountOnly(0, { refresh: reload })
    this.recordDegraded(accountResult)
    this.toolkit.hydrateAccountLevel(accountResult)
    const account = this.pickAccountFor()
    this.accountStore.setAccount(account)
    if (!account) {
      this.toolkit.finalize()
      return
    }
    const userKey = userKeyOf(this.accountStore.user)
    if (userKey) this.accountStore.setOldSelectedAccount(userKey, Number(account.id))
    this.hooks.identify?.(this.identifyContext())
    await this.loadWorkspaceLevel(reload)
    this.toolkit.finalize()
  }

  /** Pick + fetch + hydrate the workspace level (shared by the two account-then-workspace paths). */
  private async loadWorkspaceLevel(
    reload: boolean,
    skipWorkspaceId?: number | string,
  ): Promise<void> {
    const ws = this.toolkit.pickWorkspace(
      undefined,
      skipWorkspaceId === undefined ? {} : { exclude: skipWorkspaceId },
    )
    if (!ws) return
    const includeCompositions = this.isCompositionsRoute()
    this.commitWorkspace(ws)
    const accountId = Number(this.accountStore.accountId)
    const wsResult = await this.bootstrapService.bootInitialWorkspace(accountId, ws.id, {
      refresh: reload,
      includeCompositions,
    })
    this.recordDegraded(wsResult)
    this.toolkit.hydrateWorkspaceLevel(wsResult)
    this.hooks.syncWorkspaceLocale?.()
    this.startDeferredCompositions(accountId, ws.id, reload, includeCompositions)
  }

  private setAccount(oldAccountId: number): void {
    this.accountStore.setAccount(this.pickAccountFor(oldAccountId))
  }
  /** `pickAccount` with the optional keys omitted when absent (exactOptionalPropertyTypes). */
  private pickAccountFor(oldAccountId?: number): AccountItem | null {
    const userId = this.accountStore.userId
    const userKey = userKeyOf(this.accountStore.user)
    const rememberedAccountId =
      oldAccountId ?? (userKey ? this.accountStore.oldSelectedAccount[userKey] : undefined)
    return this.accountService.pickAccount({
      accounts: this.accountStore.accounts,
      ...(userId !== undefined ? { userId } : {}),
      ...(rememberedAccountId !== undefined ? { oldAccountId: rememberedAccountId } : {}),
    })
  }
  private commitWorkspace(ws: WorkspaceItem | null): void {
    if (!ws) return
    this.workspaceStore.setSelectedWorkspace(ws)
    const accountId = this.accountStore.accountId
    if (accountId !== undefined) this.workspaceStore.setOldWorkspaceForAccount(accountId, ws.id)
  }

  // Deferred compositions (v3 `:328-351`) — `isCurrentWorkspace` staleness guard preserved EXACTLY.
  private startDeferredCompositions(
    accountId: number,
    workspaceId: number | string,
    reload: boolean,
    includeCompositions: boolean,
  ): void {
    if (includeCompositions) return
    void this.runDeferredCompositions(accountId, workspaceId, reload)
  }

  private async runDeferredCompositions(
    accountId: number,
    workspaceId: number | string,
    reload: boolean,
  ): Promise<void> {
    try {
      const result = await this.bootstrapService.bootCompositionsOnly(accountId, workspaceId, {
        refresh: reload,
      })
      // Staleness guard first — a late response for a workspace we already left records nothing.
      if (!this.isCurrentWorkspace(accountId, workspaceId)) return
      this.recordDegraded(result)
      if (!result.compositions) return
      this.dispatch(result, COMPOSITIONS_SECTIONS)
      const didHydrate = Array.isArray(result.compositions) && result.compositions.length > 0
      if (didHydrate && this.isCurrentWorkspace(accountId, workspaceId)) this.toolkit.finalize()
    } catch (error) {
      this.hooks.onDeferredError?.(error)
    }
  }

  /** BE-ACC-06: stash which sections this response could not resolve (`failed`/`cold`) and
   *  whether each is retryable. A boot that reaches `ready` with entries recorded is a DEGRADED
   *  READY — data-layer only; no retry UI exists yet, and it is NEVER an error or a logout.
   *  (`?? []` tolerates hand-rolled partial results in specs; the mapper always fills it.) */
  private recordDegraded(result: MappedBootstrapResult): void {
    this.session.recordDegradedSections(result.degradedSections ?? [])
  }

  private isCurrentWorkspace(accountId: number, workspaceId: number | string): boolean {
    return (
      String(this.accountStore.accountId ?? '') === String(accountId) &&
      String(this.workspaceStore.workspaceId ?? '') === String(workspaceId)
    )
  }

  // Error taxonomy (ledger A2 — `ApiError.code`/`kind`, never prose; NEVER auto-logout).
  private async handleBootError(error: unknown): Promise<void> {
    const apiError = toApiError(error)
    const outcome = classifyBootFailure(apiError, this.hooks.onUnauthorized !== undefined)
    // The ONE trace a failed boot leaves (ORP-22838): every branch below used to be silent, so a
    // report could say "status error, no console error" and carry no reason at all.
    warnBootFailed(outcome, apiError)
    // A CANCELED boot is a session teardown, not a failure: the only aborts are auth's `abortAll()`
    // (logout, the global 401 handler, the cross-tab reset, a handoff swap) and shell navigation,
    // and each already owns the state it left. `AuthLogoutFlow.resetSession()` resets this store to
    // `idle` and THEN aborts, so `markError` here would overwrite that logout and strand an expired
    // session on the boot-error splash instead of the login screen.
    if (outcome === 'canceled') return
    // 401 bubbles to auth's onUnauthorized (06); auth's logout resets the session. 07 never logs
    // out. An unwired caller instead falls through to the error surface below — a swallowed 401
    // would strand the session in 'booting' (infinite splash, no retry, no logout).
    if (outcome === 'reauthenticate') {
      this.hooks.onUnauthorized?.(apiError)
      return
    }
    // No accessible account (404) / account_id required (400) → recoverable empty state, `ready`.
    if (outcome === 'emptyAccount') {
      await this.recoverEmptyAccount()
      this.session.markReady()
      return
    }
    // Any other non-auth failure → `error` + retry surface (contract §14). NO logout.
    this.session.markError(apiError)
  }
  private async recoverEmptyAccount(): Promise<void> {
    if (!this.accountStore.user) {
      try {
        this.accountStore.setUser(await this.accountService.fetchUser())
      } catch {
        // identity fetch is best-effort; sysadmin defaults to false when it fails.
      }
    }
    await this.hooks.onNoAccessibleAccount?.({ isSysAdmin: this.accountStore.isSysAdmin })
    this.toolkit.finalize()
  }

  private getCachedAccountId(): number | undefined {
    if (this.accountStore.account?.id) return Number(this.accountStore.account.id)
    const userKey = userKeyOf(this.accountStore.user)
    if (userKey) {
      const old = this.accountStore.oldSelectedAccount?.[userKey]
      if (old) return Number(old)
    }
    return undefined
  }
  private getCachedWorkspaceId(): number | string | undefined {
    if (this.workspaceStore.workspace?.id) return this.workspaceStore.workspace.id
    const accountId = this.accountStore.account?.id ?? this.accountStore.accountId
    if (accountId !== undefined) return this.workspaceStore.oldWorkspaceForAccount[accountId]
    return undefined
  }

  private identifyContext(): BootstrapIdentifyContext {
    return {
      user: this.accountStore.user,
      account: this.accountStore.account,
      accountDetail: this.accountStore.accountDetail,
    }
  }
}

/** What a failed boot resolves to. Pure so the one log site below can name it before it happens. */
type BootFailureOutcome = 'canceled' | 'reauthenticate' | 'emptyAccount' | 'error'

function classifyBootFailure(error: ApiError, canReauthenticate: boolean): BootFailureOutcome {
  if (error.kind === 'canceled') return 'canceled'
  if (error.kind === 'unauthorized' && canReauthenticate) return 'reauthenticate'
  if (isNoAccount(error)) return 'emptyAccount'
  return 'error'
}

/** The single structured trace a failed boot leaves behind (ORP-22838). A `console.warn`, not a
 *  throw or a toast: three of the four outcomes are recoveries and the shell renders its own
 *  surface for the fourth. Sentry keeps it as a breadcrumb (`installSentry` captures console);
 *  07 may not import `app/providers` to record one directly. */
function warnBootFailed(outcome: BootFailureOutcome, error: ApiError): void {
  console.warn('[orphex] account bootstrap failed', {
    outcome,
    kind: error.kind,
    status: error.status,
    code: error.code,
    detail: error.detail,
  })
}

function isCode(error: unknown, code: string): boolean {
  return ApiError.isApiError(error) && error.code === code
}
/** 404 `NO_ACCESSIBLE_ACCOUNT` OR 400 `ACCOUNT_ID_REQUIRED` — both land on the sysadmin/empty
 *  branch (contract §6/§14). */
function isNoAccount(error: unknown): boolean {
  return isCode(error, NO_ACCESSIBLE_ACCOUNT) || isCode(error, ACCOUNT_ID_REQUIRED)
}
